import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import {
  DxTradeInstrumentDetails,
  DxTradeInstrumentDetailsResponseItem,
  DxTradeInstrumentWithTradingMeta,
  DxTradeTradingHour,
  DxtradeInstrumentQuery,
} from '@model/DxTrade.types'

const DEFAULT_PAGE_SIZE = 8

type TradingHoursLookup = Record<
  string,
  {
    tradingHours: DxTradeTradingHour[] | null
    description: string | null
    priceIncrement: number | null
    currency: string | null
  }
>

async function fetchTradingHoursBatch(
  apiClient: ClientApiClient,
  symbols: string[]
): Promise<TradingHoursLookup> {
  if (!symbols.length) {
    return {}
  }

  const response = await apiClient.getDxtradeInstruments({
    symbols: symbols.join(','),
  })

  return (response ?? []).reduce<TradingHoursLookup>((acc, instrument) => {
    const symbol = instrument.symbol || ''
    if (!symbol) return acc

    acc[symbol] = {
      tradingHours: instrument.tradingHours ?? null,
      description: instrument.description ?? null,
      priceIncrement: instrument.priceIncrement ?? null,
      currency: instrument.currency ?? null,
    }

    return acc
  }, {})
}

function mergeTradingHours(
  items: DxTradeInstrumentDetailsResponseItem[],
  tradingHoursLookup: TradingHoursLookup
): DxTradeInstrumentWithTradingMeta[] {
  if (!items.length) {
    return items
  }

  return items.map((item) => {
    const symbol = item.symbol ?? ''
    const supplemental = symbol ? tradingHoursLookup[symbol] : undefined
    if (!supplemental) {
      return item
    }

    const description =
      (item as Partial<DxTradeInstrumentDetails>).description ?? supplemental.description ?? null

    return {
      ...item,
      tradingHours: supplemental.tradingHours,
      description,
      priceIncrement: supplemental.priceIncrement,
      currency: supplemental.currency,
    }
  })
}

async function hydrateTradingHours(
  apiClient: ClientApiClient,
  items: DxTradeInstrumentDetailsResponseItem[],
  cache: TradingHoursLookup
): Promise<TradingHoursLookup> {
  const symbols = items.map((item) => item.symbol || '').filter(Boolean)
  const missingSymbols = Array.from(new Set(symbols)).filter((symbol) => !cache[symbol])

  if (!missingSymbols.length) {
    return cache
  }

  const fetched = await fetchTradingHoursBatch(apiClient, missingSymbols)
  return { ...cache, ...fetched }
}

function createTradingHoursCache(): TradingHoursLookup {
  return {}
}

export type UseDxtradeInstrumentFeedOptions = {
  limit?: number
  symbols?: string
  symbolDescription?: string
  assetClass?: string
  types?: string[]
}

export type UseDxtradeInstrumentFeedResult = {
  instruments: DxTradeInstrumentWithTradingMeta[]
  isInitialLoading: boolean
  isLoadingMore: boolean
  hasNext: boolean
  error: unknown
  loadNext: () => void
  reload: () => void
}

export const useDxtradeInstrumentFeed = (
  accountCode?: string,
  options?: UseDxtradeInstrumentFeedOptions
): UseDxtradeInstrumentFeedResult => {
  const apiClient = useApiClient(ClientApiClient)
  const [instruments, setInstruments] = useState<DxTradeInstrumentWithTradingMeta[]>([])
  const [isInitialLoading, setIsInitialLoading] = useState(() => Boolean(accountCode))
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasNext, setHasNext] = useState(true)
  const [error, setError] = useState<unknown>(null)
  const inFlightRef = useRef(false)
  const fetchVersionRef = useRef(0)
  const tradingHoursCacheRef = useRef<TradingHoursLookup>(createTradingHoursCache())

  const limit = options?.limit ?? DEFAULT_PAGE_SIZE

  const queryConfig = useMemo(() => {
    const normalizedSymbols = options?.symbols?.trim()
    const normalizedSymbolDescription = options?.symbolDescription?.trim()
    const normalizedAssetClass = options?.assetClass
    const normalizedTypes = options?.types ?? []

    const query: DxtradeInstrumentQuery = {
      limit,
    }

    if (normalizedSymbols) {
      query.symbols = normalizedSymbols
    }

    if (normalizedSymbolDescription) {
      query.symbolDescription = normalizedSymbolDescription
    }

    if (normalizedAssetClass) {
      query.assetClass = normalizedAssetClass
    }

    if (normalizedTypes.length) {
      query.types = normalizedTypes
    }

    return query
  }, [limit, options?.symbols, options?.symbolDescription, options?.assetClass, options?.types])

  const queryKey = useMemo(() => JSON.stringify(queryConfig), [queryConfig])

  const fetchPage = useCallback(
    async (startFrom: number, mode: 'initial' | 'next') => {
      if (!accountCode || inFlightRef.current) {
        return
      }

      const version = fetchVersionRef.current

      inFlightRef.current = true
      setError(null)
      if (mode === 'initial') {
        setIsInitialLoading(true)
      } else {
        setIsLoadingMore(true)
      }

      try {
        const data = await apiClient.getDxtradeAccountInstruments(accountCode, {
          ...queryConfig,
          startFrom,
          limit,
        })

        if (version !== fetchVersionRef.current) {
          return
        }

        const tradingHoursBySymbol = await hydrateTradingHours(
          apiClient,
          data,
          tradingHoursCacheRef.current
        )

        if (version !== fetchVersionRef.current) {
          return
        }

        tradingHoursCacheRef.current = tradingHoursBySymbol

        const mergedData = mergeTradingHours(data, tradingHoursCacheRef.current)

        if (mode === 'initial') {
          setInstruments(mergedData)
        } else if (mode === 'next') {
          setInstruments((prev) => {
            if (!mergedData.length) {
              return prev
            }

            return [...prev, ...mergedData]
          })
        }

        setHasNext(data.length === limit)
      } catch (err) {
        if (version !== fetchVersionRef.current) {
          return
        }
        setError(err)
        if (mode === 'initial' || mode === 'next') {
          setHasNext(false)
        }
      } finally {
        if (version === fetchVersionRef.current) {
          inFlightRef.current = false
          if (mode === 'initial') {
            setIsInitialLoading(false)
          } else {
            setIsLoadingMore(false)
          }
        }
      }
    },
    [accountCode, apiClient, queryConfig, limit]
  )

  useEffect(() => {
    fetchVersionRef.current += 1
    inFlightRef.current = false
    setIsLoadingMore(false)
    setHasNext(true)
    if (!accountCode) {
      setInstruments([])
      setIsInitialLoading(false)
      return
    }

    fetchPage(0, 'initial')
  }, [accountCode, queryKey, fetchPage])

  const loadNext = useCallback(() => {
    if (!accountCode || !hasNext || isInitialLoading || isLoadingMore) {
      return
    }

    const nextStartFrom = instruments.length
    fetchPage(nextStartFrom, 'next')
  }, [accountCode, fetchPage, hasNext, instruments.length, isInitialLoading, isLoadingMore])

  const reload = useCallback(() => {
    setHasNext(true)

    if (!accountCode) {
      return
    }

    fetchPage(0, 'initial')
  }, [accountCode, fetchPage])

  return {
    instruments,
    isInitialLoading,
    isLoadingMore,
    hasNext,
    error,
    loadNext,
    reload,
  }
}
